import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Drawer, FormField, Input } from '@/components/ui';
import { accountDisplayName } from '../Profile/accountDisplayName';
import { ResetCredentialFields } from '../components/ResetCredentialFields';
import { StatusChip } from '../Shared/cells';
import { initialsOf } from '../Shared/cellHelpers';
import type { ResetCredential } from '../useResetCredential';
import type { KeycloakAccountRow } from '../types';
import styles from './accountDialogs.module.css';

interface Props {
    open: boolean;
    target?: KeycloakAccountRow | null;
    ident: string;
    credential: ResetCredential;
    pending: boolean;
    error: string | null;
    onIdentChange: (value: string) => void;
    onClose: () => void;
    onSubmit: (options: { endSessions: boolean }) => void;
}

/**
 * EAA-1820 (frame 74): reset password drawer — target account card, credential
 * mode cards, I2 unlock warning, session purge checkbox, and audit footer.
 *
 * EAA-1779: the mode cards moved into `ResetCredentialFields`, shared with the
 * bulk dialog; the `credential` state is owned by the page so the submit
 * handler can read it.
 *
 * EAA-1804: footer swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`; the submit button keeps
 * `htmlType="submit"` + `form` so it still fires the form outside this
 * drawer's DOM subtree. The identifier field is now `FormField` + kit
 * `Input` (was a raw `<label><input>` pair), and the end-sessions checkbox
 * is now the kit `Checkbox`.
 */
export function ResetAccountDrawer({
    open,
    target,
    ident,
    credential,
    pending,
    error,
    onIdentChange,
    onClose,
    onSubmit,
}: Props) {
    const { t } = useTranslation();
    const [endSessions, setEndSessions] = useState(false);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        onSubmit({ endSessions });
    };

    const adminSet = credential.mode === 'admin_set';

    const displayName = target ? accountDisplayName(target) : '';
    const identReadOnly = Boolean(target) || ident.trim().length > 0;

    let statusTone: 'ok' | 'warn' | 'off' = 'ok';
    let statusLabel = t('console.accounts.table.statusActive');
    if (target && !target.enabled) {
        statusTone = 'off';
        statusLabel = t('console.accounts.table.statusLocked');
    }

    return (
        <Drawer
            open={open}
            onClose={onClose}
            className="acc-reset-drawer"
            title={
                <div className={styles.resetDrawerHead}>
                    <span className={styles.resetDrawerTitle}>{t('console.accounts.resetModal.title')}</span>
                    <span className={styles.resetDrawerSubtitle}>{t('console.accounts.resetModal.subtitle')}</span>
                </div>
            }
            width={520}
            footer={
                <div className={styles.resetDrawerFooter}>
                    <span className={styles.resetDrawerFooterNote}>
                        {t('console.accounts.resetModal.auditFooter')}
                    </span>
                    <Button onClick={onClose} disabled={pending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form="accounts-reset-form"
                        disabled={pending || !ident.trim() || !credential.valid}
                    >
                        {pending ? t('console.accounts.resetModal.pending') : t('console.accounts.resetModal.title')}
                    </Button>
                </div>
            }
        >
            <form id="accounts-reset-form" className={styles.resetDrawerForm} onSubmit={handleSubmit}>
                {target ? (
                    <div className={styles.resetTarget}>
                        <span className={styles.resetTargetAvatar} aria-hidden="true">
                            {initialsOf(displayName)}
                        </span>
                        <span className={styles.resetTargetMain}>
                            <strong className={styles.resetTargetName}>{displayName}</strong>
                            <span className={styles.resetTargetMeta}>
                                <code>{target.username}</code>
                                {target.email && (
                                    <>
                                        <span aria-hidden="true"> · </span>
                                        {target.email}
                                    </>
                                )}
                            </span>
                        </span>
                        <StatusChip tone={statusTone} label={statusLabel} />
                    </div>
                ) : (
                    <FormField label={t('console.accounts.resetModal.identLabel')}>
                        <Input
                            value={ident}
                            onChange={(e) => onIdentChange(e.target.value)}
                            placeholder={t('console.accounts.resetModal.identPlaceholder')}
                            readOnly={identReadOnly}
                            required
                        />
                    </FormField>
                )}

                <ResetCredentialFields credential={credential} showEmailOption disabled={pending} />

                <div className={styles.resetWarn} role="note">
                    <svg
                        className={styles.resetWarnIcon}
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        aria-hidden="true"
                    >
                        <path d="M12 4 2.5 20h19L12 4z" strokeLinejoin="round" />
                        <path d="M12 10v4M12 17h.01" strokeLinecap="round" />
                    </svg>
                    <span>
                        {t('console.accounts.resetModal.warnPrefix')}{' '}
                        <strong>{t('console.accounts.resetModal.warnBold')}</strong>{' '}
                        {t('console.accounts.resetModal.warnSuffix')}
                    </span>
                </div>

                <Checkbox
                    className={styles.resetSessions}
                    checked={endSessions}
                    onChange={(event) => setEndSessions(event.target.checked)}
                >
                    <span>
                        {t('console.accounts.resetModal.sessionsLabel')}
                        <span className={styles.lockSessionsSub}>{t('console.accounts.resetModal.sessionsSub')}</span>
                    </span>
                </Checkbox>

                {/* EAA-1779: the one-time-reveal warning belongs to the generated
                    mode only. In admin-set mode nothing is revealed — the operator
                    typed the value — so showing it there would warn about a panel
                    that never appears. */}
                {!adminSet && (
                    <p className="portal-accounts-onetime-warning">
                        {t('console.accounts.resetModal.onetimeWarning')}
                    </p>
                )}
                {error && <p className="portal-admin-error">{error}</p>}
            </form>
        </Drawer>
    );
}

export default ResetAccountDrawer;
